import { useContractorPackage } from "@hooks/crud/contractorPackages/useContractorPackages";
import { useProject } from "@hooks/crud/project/useProject";
import { update } from "@intercom/messenger-js-sdk";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { PackageType } from "catchment-common/entities/contractorPackage";
import { ContractorPackageLineItem } from "catchment-common/interfaces/contractorPackage";
import {
  createDefaultProfile,
  ProfileLineItem
} from "catchment-common/interfaces/profile";
import {
  ProjectDetailLineItem,
  ProjectLineItem
} from "catchment-common/interfaces/project";

import { useProfileContext } from "./useProfileContext";

type ProjectContextType = {
  allProjects: ProjectDetailLineItem[];
  contractorPackage?: ContractorPackageLineItem;
  createProject: (project: ProjectLineItem) => Promise<void>;
  isHeadContractorPackage: boolean;
  isProjectLoading: boolean;
  selectedProject?: ProjectLineItem;
  setSelectedProject: (project: ProjectLineItem) => void;
  updateProject: (project: ProjectLineItem) => Promise<void>;
  userProfile: ProfileLineItem;
};

export const ProjectContext = React.createContext<ProjectContextType>({
  allProjects: [],
  createProject: () => Promise.resolve(),
  isHeadContractorPackage: false,
  isProjectLoading: false,
  setSelectedProject: () => {
    console.error("setSelectedProject function not implemented");
  },
  updateProject: () => Promise.resolve(),
  userProfile: createDefaultProfile()
});

type ProjectProviderProps = {
  children: React.ReactNode;
};

export const ProjectProvider: React.FC<ProjectProviderProps> = (props) => {
  const { contractorPackageId, projectId } = useParams();
  const [isHeadContractorPackage, setIsHeadContractorPackage] =
    useState<boolean>(false);
  const [contractorPackage, setContractorPackage] =
    useState<ContractorPackageLineItem>();

  if (!projectId) {
    throw new Error("Project id is required");
  }

  const { userOrganisation, userProfile } = useProfileContext();

  if (!userProfile) {
    throw new Error("User profile is required");
  }

  const {
    createProject,
    getProject,
    isProjectLoading,
    projects,
    selectedProject,
    setSelectedProject,
    updateProject
  } = useProject();

  const { getContractorPackage, isContractorPackageLoading } =
    useContractorPackage();

  const fetchProjectData = useCallback(
    async (projectId: string, contractorPackageId: string) => {
      setSelectedProject(undefined);
      setContractorPackage(undefined);
      const [_, contractorPackageRes] = await Promise.all([
        getProject(projectId),
        getContractorPackage(contractorPackageId)
      ]);
      const selectedPackage = contractorPackageRes;
      setIsHeadContractorPackage(
        selectedPackage
          ? selectedPackage.packageType === PackageType.HeadContractorPackage
          : false
      );
      setContractorPackage(selectedPackage);
    },
    [getProject, getContractorPackage]
  );

  useEffect(() => {
    if (projectId && contractorPackageId) {
      fetchProjectData(projectId, contractorPackageId);
    }
  }, [projectId, contractorPackageId, fetchProjectData]);

  useEffect(() => {
    if (selectedProject && userOrganisation) {
      update({
        company: {
          id: userOrganisation?.id,
          name: `${userOrganisation?.organisationName} - (${selectedProject?.projectName})`
        }
      });
    }
  }, [selectedProject, userOrganisation]);

  return (
    <ProjectContext.Provider
      value={{
        allProjects: projects,
        contractorPackage: contractorPackage,
        createProject,
        isHeadContractorPackage,
        isProjectLoading: isProjectLoading || isContractorPackageLoading,
        selectedProject: selectedProject || undefined,
        setSelectedProject,
        updateProject,
        userProfile
      }}
    >
      {props.children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => React.useContext(ProjectContext);
